import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { createContext, provide } from '@lit/context'
import { required } from 'components/shared/required/required.js'
import {
  fetcher,
  type RefreshShipmentMethodsForBasketBody,
  type SearchForDropOffLocationsBody, TemplateDeliveryAddress,
  type TemplatePriceData,
  type UpdateShipmentMethodBody
} from './api/fetcher.js'
import {
  DropOffMethods,
  type DeliveryMatchFailure,
  type DeliveryMethods as DeliveryMethodsDTO,
  type DropoffMethodDTO,
} from '@cloudsuite/cs-ordering-defs'
import { ShipmentMethods, type DeliveryMethods, type DropoffMethod, type DropoffMethods } from './api/shipment-methods.js'
import { parseDropoffMethod, parseShipmentMethods } from './api/parser.js'
import { is } from 'typia'
import { GOOGLE_GEOCODER_API, GoogleGeocodeRequest, GoogleReverseGeocodeRequest, type GoogleGeocodeApi } from './api/google-geocoder-api.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import type { Geocodes, ReverseGeocodes } from './api/google-geocoder.js'
import { RequestShipmentMethodsForBasketError, SearchForDropOffLocationsError, UpdateShipmentMethodError, type DeliveryMatchError } from './errors.js'
import { runWithSkeletonTiming } from './search-skeleton-timing.js'

export type DeliveryMethodGroupType = 'delivery' | 'dropoff'

export const NO_SELECTED_METHOD = Symbol('NO_SELECTED_DELIVERY_METHOD')
export const NO_SELECTED_METHOD_GROUP = Symbol('NO_SELECTED_METHOD_GROUP')

import type { GeolocationType } from './geolocation-type.js'

const CENTER_NL = {
  lat: '52.10192656788224',
  lon: '5.646937151156543'
}

const COUNTRY_CENTERS: Record<string, GeolocationType> = {
  BE: {
    lat: '50.5039',
    lon: '4.4699'
  },
  NL: CENTER_NL
}

export type DeliveryMatchSelectedMethodContextType = typeof NO_SELECTED_METHOD | string
export type DeliveryMatchSelectedMethodGroupContextType = typeof NO_SELECTED_METHOD_GROUP | DeliveryMethodGroupType
export type DeliveryMatchDeliveryMethodsContextType = ShipmentMethods['deliveryMethods']
export type DeliveryMatchDropoffMethodsContextType = ShipmentMethods['dropoffMethods']
export type DeliveryMatchErrorContextType = DeliveryMatchError | null
export type DeliveryMatchActionsContextType = {
  fetchShipmentMethods: () => Promise<void>
  updateShipmentMethod: () => Promise<boolean>
  setSelectedMethod: (methodId: string | typeof NO_SELECTED_METHOD) => void
  setSelectedMethodGroup: (methodGroup: DeliveryMethodGroupType | typeof NO_SELECTED_METHOD_GROUP) => void
  setGeolocationPosition: (geolocation: GeolocationType) => void
  getGeolocationFromZipcode: (zipcode: string) => Promise<GeolocationType>
  getZipcodeFromGeolocation: (location: GeolocationType) => Promise<string>
  setSearchedZipCode: (zipcode: string) => void
  searchShipmentMethods: (zipcode: string) => Promise<DropOffMethods>
  searchForLocation: (zipcode: string) => Promise<void>
  searchForLocationByGeolocation: (geolocation: GeolocationType) => Promise<void>
  setDropoffMethodMode: (mode: DeliveryMatchDropoffMethodModeContextType) => void
  setDropoffMethods: (methods: DropoffMethodDTO[]) => void
}

export type DeliveryMatchCurrencyContextType = {
  currency: string
  locale: `${string}-${string}`
}

export type DeliveryMatchCurrencyGeolocationType = GeolocationType | undefined
export type DeliveryMatchDropoffMethodModeContextType = 'list' | 'map'
export type DeliveryMatchDeliveryAddressContextType = TemplateDeliveryAddress
export type DeliveryMatchMethodsByCategoriesContextType = (DropoffMethods | DeliveryMethods)[]


export const DeliveryMatchSelectedMethodContext = createContext<DeliveryMatchSelectedMethodContextType>(Symbol('delivery-match-selected-method-context'))
export const DeliveryMatchSelectedMethodGroupContext = createContext<DeliveryMatchSelectedMethodGroupContextType>(Symbol('delivery-match-selected-method-group-context'))
export const DeliveryMatchDeliveryMethodsContext = createContext<DeliveryMatchDeliveryMethodsContextType>(Symbol('delivery-match-delivery-methods-context'))
export const DeliveryMatchDropoffMethodsContext = createContext<DeliveryMatchDropoffMethodsContextType>(Symbol('delivery-match-dropoff-methods-context'))
export const DeliveryMatchMethodsByCategoriesContext = createContext<DeliveryMatchMethodsByCategoriesContextType>(Symbol('delivery-match-methods-by-categories-context'))
export const DeliveryMatchErrorContext = createContext<DeliveryMatchErrorContextType>(Symbol('delivery-match-error-context'))
export const DeliveryMatchActionsContext = createContext<DeliveryMatchActionsContextType>(Symbol('delivery-match-actions-context'))
export const DeliveryMatchCurrencyContext = createContext<DeliveryMatchCurrencyContextType>(Symbol('delivery-match-currency-context'))
export const DeliveryMatchGeolocationContext = createContext<DeliveryMatchCurrencyGeolocationType>(Symbol('delivery-match-geolocation-context'))
export const DeliveryMatchSearchedZipcodeContext = createContext<string>(Symbol('delivery-match-search-zipcode-context'))
export const DeliveryMatchDropoffMethodModeContext = createContext<DeliveryMatchDropoffMethodModeContextType>(Symbol('delivery-match-dropoff-method-mode-context'))
export const DeliveryMatchDeliveryAddressContext = createContext<DeliveryMatchDeliveryAddressContextType>(Symbol('delivery-match-delivery-address-context'))
export const DeliveryMatchIsLoadingMethodsContext = createContext<boolean>(Symbol('delivery-match-is-loading-methods-context'))
export const DeliveryMatchIsSearchingContext = createContext<boolean>(Symbol('delivery-match-is-searching-context'))

const dropoffMethodUniqueSet = new Set()
@customElement('delivery-match-provider')
export class DeliveryMatchProvider extends LitElement {
  // Methods
  public fetchShipmentMethods = async () => {
    const paramsFromProvider = this.getParams()

    await fetcher.fetch('RefreshShipmentMethodsForBasket', paramsFromProvider).then((data: DeliveryMethodsDTO) => {
      if (data) {
        if (is<DeliveryMethodsDTO>(data)) {
          const { methodsByCategory } = parseShipmentMethods(data)
          this.deliveryMethods = methodsByCategory.filter(m => is<DeliveryMethods>(m))[0]?.methods ?? []
          this.methodsByCategories = methodsByCategory
          dropoffMethodUniqueSet.clear()
          this.dropoffMethods = (methodsByCategory.filter(m => is<DropoffMethods>(m))[0]?.methods ?? []).filter(this.filterUniqueDropoffMethods)
        } else {
          this.error = new RequestShipmentMethodsForBasketError()
        }
      }
      this.isLoadingMethods = false
    }).catch((error: Error) => {
      console.error(this.localName, error)
      this.error = new RequestShipmentMethodsForBasketError()
      this.isLoadingMethods = false
    })
  }

  private filterUniqueDropoffMethods(value: DropoffMethodDTO | DropoffMethod) {
    const key = `${value.address.latitude}_${value.address.longitude}`

    if (dropoffMethodUniqueSet.has(key)) return false
    dropoffMethodUniqueSet.add(key)

    return true
  }

  public updateShipmentMethod = async () => {
    const params: UpdateShipmentMethodBody = {
      shopId: this.shopId,
      methodId: this.selectedMethod as string,
      basketId: this.basketId
    }

    const success = await fetcher.fetch('UpdateShipmentMethod', params)
      .then((response: any) => {
        if (is<DeliveryMatchFailure>(response)) {
          console.error(this.localName, response)
          return false
        }
        return true
      })
      .catch((error: Error) => {
        console.error(this.localName, error)
        return false
      })

    if (success) {
      this.error = null
    } else {
      this.error = new UpdateShipmentMethodError()
    }

    return success
  }

  public searchShipmentMethods = async (zipcode: string): Promise<DropOffMethods> => {
    const params: SearchForDropOffLocationsBody = {
      shopId: this.shopId,
      basketId: this.basketId,
      countryCode: this.deliveryAddress.country_code,
      zipcode
    }

    const response = await fetcher.fetch('SearchForDropOffLocations', params)

    if (!is<DropOffMethods>(response)) {
      console.error(this.localName, response)
      return new DropOffMethods([])
    }

    return response
  }

  public searchForLocation = async (zipcode: string): Promise<void> => {
    this.setSearchedZipCode(zipcode)
    this.error = null
    this.dropoffMethods = []

    await this.runWithSearchingState(async () => {
      const geo = await this.getGeolocationFromZipcode(zipcode)
      this.setGeolocationPosition(geo)
      const result = await this.searchShipmentMethods(zipcode)

      if (result.methods.length === 0) {
        this.error = new SearchForDropOffLocationsError()
        return
      }

      this.setDropoffMethods(result.methods)
    })
  }

  public searchForLocationByGeolocation = async (geolocation: GeolocationType): Promise<void> => {
    this.error = null
    this.dropoffMethods = []
    this.setGeolocationPosition(geolocation)

    await this.runWithSearchingState(async () => {
      const zipcode = await this.getZipcodeFromGeolocation(geolocation)
      this.setSearchedZipCode(zipcode)

      if (!zipcode) {
        this.error = new SearchForDropOffLocationsError()
        return
      }

      const result = await this.searchShipmentMethods(zipcode)

      if (result.methods.length === 0) {
        this.error = new SearchForDropOffLocationsError()
        return
      }

      this.setDropoffMethods(result.methods)
    })
  }

  private runWithSearchingState = async (fn: () => Promise<void>): Promise<void> => {
    const setSearching = (value: boolean) => { this.isSearching = value }
    await runWithSkeletonTiming(setSearching, fn)
  }

  public setDropoffMethods = (methods: DropoffMethodDTO[]) => {
    if (!(this.deliveryMethods || this.dropoffMethods)) {
      return
    }

    dropoffMethodUniqueSet.clear()
    this.dropoffMethods = methods.filter(this.filterUniqueDropoffMethods).map((m) => parseDropoffMethod(m))
  }

  public setSelectedMethod = (selectedMethod: string | typeof NO_SELECTED_METHOD) => {
    if (this.selectedMethod !== selectedMethod) {
      this.selectMethod(selectedMethod)
      this.error = null
    }
  }

  public setSelectedMethodGroup = (selectedMethodGroup: DeliveryMethodGroupType | typeof NO_SELECTED_METHOD_GROUP) => {
    if (this.selectedMethodGroup === selectedMethodGroup) return
    this.selectedMethodGroup = selectedMethodGroup
    this.selectedMethod = NO_SELECTED_METHOD
    this.error = null
  }

  public selectMethod = async (methodId: string | typeof NO_SELECTED_METHOD) => {
    this.selectedMethod = methodId
  }

  public getParams = (): RefreshShipmentMethodsForBasketBody => {
    return {
      basketId: this.basketId,
      address: this.deliveryAddress,
      languageCode: this.languageCode,
      currencyCode: this.currencyCode,
      shopId: this.shopId,
      locale: this.locale,
      priceData: this.priceData,
    }
  }

  public setGeolocationPosition = (geo: GeolocationType) => {
    this.geolocationPosition = geo
  }

  public getGeolocationFromZipcode = async (zipcodeAndCountry: string) => {
    const result = await this.geocodeApi.geocode(new GoogleGeocodeRequest(zipcodeAndCountry, this.proxy))

    if (is<Geocodes>(result) && result.results.length > 0) {
      return {
        lat: `${result.results[0]?.geometry?.location?.lat || 0}`,
        lon: `${result.results[0]?.geometry?.location?.lng || 0}`,
      }
    } else {
      console.warn('Could not find any geocodes for this address')
      return COUNTRY_CENTERS[this.deliveryAddress.country_code] ?? CENTER_NL
    }
  }

  public getZipcodeFromGeolocation = async (geolocation: GeolocationType) => {
    const result = await this.geocodeApi.geocode(new GoogleReverseGeocodeRequest(geolocation, this.proxy))

    if (is<ReverseGeocodes>(result) && result.results && result.results.length > 0 && result.results[0]?.address_components) {
      return result.results[0].address_components.filter(a => a.types.indexOf('postal_code') != -1)[0]?.long_name ?? ''
    } else {
      console.warn('Could not find any zipcode for this location')
      return ''
    }
  }

  private initialGeolocationFetch = async () => {
    const zipcodeAndCity = `${this.deliveryAddress.zipcode},${this.deliveryAddress.city}`
    const geolocation = await this.getGeolocationFromZipcode(zipcodeAndCity)
    this.setGeolocationPosition(geolocation)
  }

  public setSearchedZipCode = (zip: string) => {
    this.searchedZipCode = zip
  }

  private setDropoffMethodMode = (mode: DeliveryMatchDropoffMethodModeContextType) => {
    this.dropoffMethodMode = mode
  }

  // Properties & State

  @required()
  @property({ attribute: 'basket-id' })
  basketId!: string

  @property({ attribute: 'delivery-address', type: Object })
  deliveryAddress!: TemplateDeliveryAddress

  @property({ attribute: 'price-data', type: Object })
  priceData: TemplatePriceData | null = null

  @required()
  @property({ attribute: 'language-code' })
  languageCode!: string

  @required()
  @property({ attribute: 'currency-code' })
  currencyCode!: string

  @required()
  @property({ attribute: 'shop-id', type: Number })
  shopId!: number

  @required()
  @property()
  locale!: `${string}-${string}`

  @required()
  @property()
  proxy!: string

  // Providers

  @provide({ context: DeliveryMatchActionsContext })
  actions!: DeliveryMatchActionsContextType

  @provide({ context: DeliveryMatchSelectedMethodContext })
  selectedMethod: DeliveryMatchSelectedMethodContextType = NO_SELECTED_METHOD

  @provide({ context: DeliveryMatchSelectedMethodGroupContext })
  selectedMethodGroup: DeliveryMatchSelectedMethodGroupContextType = NO_SELECTED_METHOD_GROUP

  @provide({ context: DeliveryMatchDeliveryMethodsContext })
  deliveryMethods: DeliveryMatchDeliveryMethodsContextType = []

  @provide({ context: DeliveryMatchDropoffMethodsContext })
  dropoffMethods: DeliveryMatchDropoffMethodsContextType = []
  
  @provide({ context: DeliveryMatchMethodsByCategoriesContext })
  methodsByCategories: DeliveryMatchMethodsByCategoriesContextType = []

  @provide({ context: DeliveryMatchErrorContext })
  error: DeliveryMatchErrorContextType = null

  @provide({ context: DeliveryMatchCurrencyContext })
  currencyContext!: DeliveryMatchCurrencyContextType

  @provide({ context: DeliveryMatchSearchedZipcodeContext })
  searchedZipCode!: string

  @provide({ context: DeliveryMatchGeolocationContext })
  geolocationPosition?: GeolocationType

  @provide({ context: DeliveryMatchDropoffMethodModeContext })
  dropoffMethodMode: DeliveryMatchDropoffMethodModeContextType = 'list'

  @inject(GOOGLE_GEOCODER_API)
  geocodeApi!: GoogleGeocodeApi

  @provide({ context: DeliveryMatchDeliveryAddressContext })
  deliveryAddressContext!: DeliveryMatchDeliveryAddressContextType

  @provide({ context: DeliveryMatchIsLoadingMethodsContext })
  isLoadingMethods: boolean = true

  @provide({ context: DeliveryMatchIsSearchingContext })
  isSearching: boolean = false


  // Overrides

  override connectedCallback(): void {
    super.connectedCallback()
    this.fetchShipmentMethods()

    // Initialize actions object once to prevent recreation on every render
    if (!this.actions) {
      this.actions = {
        fetchShipmentMethods: this.fetchShipmentMethods,
        updateShipmentMethod: this.updateShipmentMethod,
        setSelectedMethod: this.setSelectedMethod,
        setSelectedMethodGroup: this.setSelectedMethodGroup,
        setGeolocationPosition: this.setGeolocationPosition,
        getGeolocationFromZipcode: this.getGeolocationFromZipcode,
        setSearchedZipCode: this.setSearchedZipCode,
        searchShipmentMethods: this.searchShipmentMethods,
        searchForLocation: this.searchForLocation,
        searchForLocationByGeolocation: this.searchForLocationByGeolocation,
        getZipcodeFromGeolocation: this.getZipcodeFromGeolocation,
        setDropoffMethodMode: this.setDropoffMethodMode,
        setDropoffMethods: this.setDropoffMethods
      }
    }

    // Initialize currencyContext once to prevent recreation on every render
    // Always create a new object to ensure Lit detects changes
    this.currencyContext = {
      currency: this.currencyCode,
      locale: this.locale
    }

    this.deliveryAddressContext = this.deliveryAddress

    this.searchedZipCode = ''
    this.initialGeolocationFetch()
  }

  protected override createRenderRoot(): this {
    return this
  }
}
